import { CircularProgress } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { AxiosResponse } from 'axios';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { IAuthState, useAuthProvider } from '../../Components/AuthProvider';
import { StringField } from '@components/Fields/StringField';
import { PasswordField } from '@components/Fields/PasswordField';
import useMessage from '@hooks/useMessage';
import { IAuthRequest, useAuthService } from '@services/AuthService';
import useLoading from '@hooks/useLoading';
import { yup } from '@validations/Yup';
import { useForm } from '@hooks/useForm';
import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { useValidation } from '@hooks/useValidation';
import { useField } from '@hooks/useField';

type LoginProps = {}

export function Login (props: LoginProps) {
    const authProvider = useAuthProvider();
    const history = useHistory();

    const pushToHome = React.useCallback(() => {
        history.push("/");
    }, [history]);

    if (!!authProvider?.hasAuth) {
        pushToHome();
    }

    const classes = useStyles();
    const authService = useAuthService();
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<IAuthRequest>({
        user: useField(''),
        pass: useField('')
    });

    const validation = useValidation({
        user: yup.string().required(),
        pass: yup.string().required()
    });

    const handleResponse = React.useCallback((response: AxiosResponse<IAuthState>) => {
        authProvider?.setState(response.data);
        pushToHome();
    }, [authProvider, pushToHome]);

    const handleLogin = React.useCallback(() =>{
        if (!loading.isLoading) {
            const values = form.getInputValues();

            validation.validate(values)
                .then(_ => {
                    loading.start();
                    authService.login(values)
                        .then(handleResponse)
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup)
        }
    }, [authService, form, handleResponse, loading, message, validation]);

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <div className={classes.paper}>
                <Avatar className={classes.avatar}>
                    <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5">MOBI - Login</Typography>

                <form className={classes.form} noValidate>
                    <StringField 
                        field={form.fields.user}
                        label='Usuário'
                        required
                    />

                    <PasswordField
                        field={form.fields.pass}
                        label='Senha'
                        required
                    />

                    {loading.isLoading
                    ? (
                        <Container className={classes.containerProgress}><CircularProgress /></Container>
                    )
                    : (
                        <PrimaryButton
                            fullWidth
                            className={classes.submit}
                            onClick={handleLogin}
                        >
                            Entrar
                        </PrimaryButton>
                    )}
                </form>
            </div>
        </Container>
    );
}

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),

        '& .MuiFormControl-root':
        {
            marginBottom: theme.spacing(2)
        },
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },

    containerProgress: {
        textAlign: 'center',
        marginTop: theme.spacing(2)
    }
}));
